import { Executor } from '../executor';

export interface StorageEntry {
  _: string;
  v: string;
  t: number;
  s: number;
}

export interface KeyValueItem {
  key: string;
  value: string;
}

export interface SessionStorageOptions {
  name?: string;
  setItemExecutor?: Executor;
}

type SessionStorageItem = { key: string; value: StorageEntry };

const defaultSetItemExecutor = new Executor({
  strategies: [
    {
      name: 'operation',
      execute: (operation) => operation(),
      canRetry: () => false,
      error: (error) => {
        if (error instanceof Error) {
          throw error;
        } else {
          throw new Error(
            `SessionStorage.setItem: failed. ${
              typeof error === 'object' ? JSON.stringify(error) : error
            }`,
          );
        }
      },
    },
  ],
});

export class SessionStorage {
  private _name: string;
  private _setItem: Executor;

  get name(): string {
    return this._name;
  }

  constructor(options: SessionStorageOptions = {}) {
    this._name = options.name ?? this.constructor.name;
    this._setItem = options.setItemExecutor ?? defaultSetItemExecutor;
  }

  setItem(key: string, value: string): void {
    this._setItem.execute(() => {
      const data: StorageEntry = { v: value, t: Date.now(), s: 0, _: this.name };
      const size = JSON.stringify(data).length;
      const item: StorageEntry = { ...data, s: size + size.toString().length - 1 };
      sessionStorage.setItem(key, JSON.stringify(item));
      return true;
    });
  }

  getItem(key: string): string | null {
    const item = this._getEntry(key);
    return item?.v ?? null;
  }

  getEntry(key: string): StorageEntry | null {
    return this._getEntry(key);
  }

  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  clear(ageMs?: number): void {
    for (const { key, value } of this._entries()) {
      const diff = Date.now() - value.t;
      if (ageMs != null && diff <= ageMs) {
        continue;
      }
      this.removeItem(key);
    }
  }

  key(index: number): string | null {
    return sessionStorage.key(index);
  }

  size(): number {
    let total = 0;
    for (const { key, value } of this._entries()) {
      total += (value?.s ?? 0) + key.length;
    }
    return total;
  }

  length(): number {
    return Array.from(this).length;
  }

  *[Symbol.iterator]() {
    for (const { key } of this._entries()) {
      yield key;
    }
  }

  private *_entries(): Generator<SessionStorageItem> {
    for (const key of Object.keys(sessionStorage)) {
      const value = this._getStorageValue(key);
      if (this._isStorageEntry(value)) {
        if (value._ === this._name) {
          yield { key, value };
        }
      }
    }
  }

  private _getEntry(key: string): StorageEntry | null {
    const value = this._getStorageValue(key);
    if (value == null) {
      return null;
    }

    if (this._isStorageEntry(value)) {
      return value;
    }

    // convert to StorageEntry
    const strValue = typeof value === 'string' ? value : JSON.stringify(value);
    this.setItem(key, strValue);
    return this._getEntry(key);
  }

  private _getStorageValue(key: string): any | null {
    const item = sessionStorage.getItem(key);
    try {
      return item == null ? null : JSON.parse(item);
    } catch {
      return item;
    }
  }

  private _isStorageEntry(value: any) {
    const _: keyof StorageEntry = '_';
    const v: keyof StorageEntry = 'v';
    return value != null && typeof value === 'object' && _ in value && v in value;
  }
}
